import type { SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import { Box, Text, chakra } from '@chakra-ui/react';

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  profile_image: string | null;
  role: string;
}

interface ProfileSearchResultsProps {
  results: Profile[];
  isLoading: boolean;
  onSelect: () => void;
}

export function ProfileSearchResults({ results, isLoading, onSelect }: ProfileSearchResultsProps) {
  const navigate = useNavigate();

  if (!results.length && !isLoading) return null;

  return (
    <Box mt="2" bg="white" borderRadius="xl" boxShadow="0 8px 24px rgba(0,0,0,0.12)" border="1px solid" borderColor="volyx.mediumGray" maxH="400px" overflowY="auto">
      {isLoading ? (
        <Text p="4" textAlign="center" color="volyx.darkGray" fontSize="sm" fontFamily="body">Searching...</Text>
      ) : results.length > 0 ? (
        <Box py="2">
          {results.map((profile) => {
            const displayName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.email;
            return (
            <chakra.button
              key={profile.id}
              type="button"
              aria-label={`View profile ${displayName}`}
              onClick={() => {
                onSelect();
                navigate(`/profile?id=${profile.id}`);
              }}
              w="full"
              display="flex"
              alignItems="center"
              gap="3"
              px="4"
              py="3"
              _hover={{ bg: "volyx.lightGray" }}
              transition="color 0.15s, background-color 0.15s"
              textAlign="left"
              border="none"
              bg="transparent"
              cursor="pointer"
            >
              <chakra.img
                src={profile.profile_image || DEFAULT_PROFILE_IMAGE}
                alt=""
                w="10"
                h="10"
                borderRadius="full"
                objectFit="cover"
                onError={(e: SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.src = DEFAULT_PROFILE_IMAGE;
                }}
              />
              <Box flex="1" minW="0">
                <Text fontSize="sm" fontWeight="semibold" color="volyx.text" fontFamily="body" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {profile.first_name} {profile.last_name}
                </Text>
              </Box>
            </chakra.button>
            );
          })}
        </Box>
      ) : (
        <Text p="4" textAlign="center" color="volyx.darkGray" fontSize="sm" fontFamily="body">No profiles found</Text>
      )}
    </Box>
  );
}
